import { useMemo, useState } from "react";
import { Save } from "lucide-react";
import { api } from "../../api/client";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { todayIso } from "../leave/LeaveCalendar";
import type { UserPick } from "../../api/types";
import { SIZES, SIZE_LABEL, WORK_ORDER_STATUS_TONE, emptyInput, inputOf } from "../../api/workorders";
import type { FormDef, WorkOrder, WorkOrderInput } from "../../api/workorders";
import { SpecForm } from "./SpecForm";
import { WorkOrderActions, WorkOrderHistory } from "./WorkOrderActions";

// 工單編輯(規格 §8):上方共同欄位,中間依種類的規格勾選區,頂端標題列(儲存與各種動作),下方狀態歷程。
// 廠商與報價單連動業主 2026-10-15 拿掉了(用不到);舊單上已存的值照原樣送回,不清掉。
// 已寄出 / 已完成 / 已作廢唯讀(整個表單包在 disabled 的 fieldset 裡),原因寫在上面。

const READ_ONLY_REASON: Partial<Record<WorkOrder["status"], string>> = {
  SENT: "已寄出的工單不能修改,要改請按「複製」開一張新的",
  COMPLETED: "已完成的工單不能修改,要改請按「複製」開一張新的",
  VOID: "已作廢的工單不能修改",
};

export interface WorkOrderEditorProps {
  form: FormDef;
  workOrder: WorkOrder | null;
  users: UserPick[];
  currentUserId: number | null;
  usersFailed?: boolean;
  onSaved: (wo: WorkOrder) => void;
  onOpenWorkOrder: (id: number) => void;
  onDeleted?: () => void;
}

export function WorkOrderEditor({ form, workOrder, users, currentUserId, usersFailed = false, onSaved, onOpenWorkOrder, onDeleted }: WorkOrderEditorProps) {
  const toast = useToast();
  const saved = useMemo(() => (workOrder ? inputOf(workOrder) : emptyInput(currentUserId, todayIso())), [workOrder, currentUserId]);
  const [draft, setDraft] = useState<WorkOrderInput>(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readOnlyReason = workOrder ? READ_ONLY_REASON[workOrder.status] ?? null : null;
  const readOnly = readOnlyReason !== null;
  const dirty = workOrder === null || JSON.stringify(draft) !== JSON.stringify(saved);
  const set = <K extends keyof WorkOrderInput>(key: K, next: WorkOrderInput[K]) => setDraft((d) => ({ ...d, [key]: next }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const got = workOrder
        ? await api.put<WorkOrder>(`/work-orders/${workOrder.id}`, draft)
        : await api.post<WorkOrder>("/work-orders", { form_type: form.code, ...draft });
      const reset = workOrder !== null && workOrder.status !== "DRAFT" && got.status === "DRAFT";
      toast.push(reset ? "內容改了,這張工單回到草稿,需要重新送覆核" : "已儲存", reset ? "info" : "success");
      setDraft(inputOf(got));
      onSaved(got);
    } catch (e) {
      setError(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const handlerOptions = [{ value: "", label: "未指派" }, ...users.map((u) => ({ value: String(u.id), label: u.name }))];
  if (draft.handler_user_id !== null && !users.some((u) => u.id === draft.handler_user_id)) {
    handlerOptions.splice(1, 0, { value: String(draft.handler_user_id), label: workOrder?.handler_name || `同仁 #${draft.handler_user_id}` });
  }

  const titleNode = (
    <>
      {workOrder ? <span className="tabular text-base font-semibold text-[var(--text)]">{workOrder.number}</span>
        : <span className="text-base font-semibold text-[var(--text)]">新工單</span>}
      <h2 className="m-0 text-base font-normal text-[var(--text-2)]">{form.name}</h2>
      {workOrder ? <Badge tone={WORK_ORDER_STATUS_TONE[workOrder.status]}>{workOrder.status_label}</Badge> : null}
    </>
  );
  const leadNode = (
    <>
      {workOrder && dirty && !readOnly ? <span className="text-sm text-[var(--warn)]">有未存檔的修改</span> : null}
      <Button type="button" size="sm" variant="primary" icon={<Save aria-hidden="true" size={16} />} onClick={() => void save()} loading={saving} disabled={readOnly || !dirty}>儲存</Button>
    </>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {workOrder ? (
        <WorkOrderActions
          workOrder={workOrder}
          dirty={dirty}
          title={titleNode}
          lead={leadNode}
          onChanged={(got) => {
            setDraft(inputOf(got));
            onSaved(got);
          }}
          onOpenWorkOrder={onOpenWorkOrder}
          onDeleted={onDeleted}
        />
      ) : (
        <section aria-label="工單動作" className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {titleNode}
            <div className="ml-auto flex flex-wrap items-center gap-2">{leadNode}</div>
          </div>
        </section>
      )}
      {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      {usersFailed ? <p role="status" className="m-0 text-sm text-[var(--warn)]">同仁名單載入失敗,承辦人可能無法變更</p> : null}
      {readOnlyReason ? <p role="status" className="m-0 rounded-[var(--radius)] bg-[var(--surface-2)] px-3 py-2 text-sm">{readOnlyReason}</p> : null}
      {workOrder?.status === "DRAFT" && workOrder.review_note ? (
        <p role="status" className="m-0 rounded-[var(--radius)] border border-[var(--warn)] px-3 py-2 text-sm">退回原因:{workOrder.review_note}</p>
      ) : null}

      <fieldset disabled={readOnly} className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
        <section aria-label="基本資料" className="grid grid-cols-1 gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3 sm:grid-cols-2">
          <Input label="品名" value={draft.title} required onChange={(e) => set("title", e.target.value)} />
          <Input label="印製數量" value={draft.quantity} placeholder="例:500 本" onChange={(e) => set("quantity", e.target.value)} />
          <Input label="發印日期" type="date" value={draft.issue_date ?? ""} onChange={(e) => set("issue_date", e.target.value || null)} />
          <Input label="交件日期" type="date" value={draft.due_date ?? ""} onChange={(e) => set("due_date", e.target.value || null)} />
          <fieldset className="m-0 flex flex-wrap items-center gap-3 border-0 p-0">
            <legend className="mb-1 text-sm font-medium text-[var(--text-2)]">尺寸</legend>
            {SIZES.map((size) => (
              <label key={size} className="flex items-center gap-1 text-sm">
                <input type="radio" name="work-order-size" checked={draft.size === size} onChange={() => set("size", size)} />
                {SIZE_LABEL[size]}
              </label>
            ))}
            {draft.size === "OTHER" ? (
              <Input aria-label="尺寸說明" value={draft.size_other} onChange={(e) => set("size_other", e.target.value)} />
            ) : null}
          </fieldset>
          <Select label="承辦人" value={draft.handler_user_id === null ? "" : String(draft.handler_user_id)} options={handlerOptions}
            onChange={(e) => set("handler_user_id", e.target.value ? Number(e.target.value) : null)} />
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-sm font-medium text-[var(--text-2)]">備註</span>
            <textarea rows={3} value={draft.note} onChange={(e) => set("note", e.target.value)}
              className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm" />
          </label>
        </section>

        <section aria-label="印製規格" className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3">
          <h3 className="m-0 text-base font-semibold text-[var(--text)]">印製規格</h3>
          <SpecForm form={form} value={draft.spec} onChange={(spec) => set("spec", spec)} disabled={readOnly} />
        </section>
      </fieldset>

      {workOrder ? <WorkOrderHistory history={workOrder.history} /> : null}
    </div>
  );
}
